/**
 * The Ledger Banner: a state of the page or of a region, said once, at the top
 * of the content, full width. A 1px border on the soft tint of its tone, a
 * 16px icon, the text, and an optional action or link at the end.
 *
 * Tones: `info`, `warn`, `err`, `ok`, and `neutral` for a plain note. It is
 * drawn by the `alert` recipe (src/theme/recipes/data.ts), so a raw
 * `Alert.Root` with a `status` looks the same; new code uses this.
 *
 * Only an `err` banner for a failure of the page or region (a load, a save)
 * is an alert to assistive technology (`role="alert"`); every other banner is
 * a note, so a page state is not announced over the person's work. An `err`
 * banner that follows the person's typing (validation of what they are
 * editing) passes `announce={false}`: a live alert there would interrupt on
 * every keystroke. Feedback for a finished action is a toast, not a banner.
 *
 * Spec: specs/design-system/ledger-data-display.feature
 */
import { Alert, type AlertRootProps, Box } from "@chakra-ui/react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";

export type BannerTone = "info" | "warn" | "err" | "ok" | "neutral";

const STATUS: Record<BannerTone, NonNullable<AlertRootProps["status"]>> = {
  info: "info",
  warn: "warning",
  err: "error",
  ok: "success",
  neutral: "neutral",
};

const ICON: Record<BannerTone, LucideIcon> = {
  info: Info,
  warn: AlertTriangle,
  err: AlertCircle,
  ok: CheckCircle2,
  neutral: Info,
};

export interface BannerProps
  extends Omit<AlertRootProps, "status" | "title" | "children"> {
  tone?: BannerTone;
  /** A short first sentence, in medium weight. */
  title?: React.ReactNode;
  /** The text. */
  children?: React.ReactNode;
  /** One action or link at the end ("Set up tracing", "Retry"). */
  action?: React.ReactNode;
  /** Replaces the tone's icon; `null` draws none. */
  icon?: React.ReactNode | null;
  /**
   * Whether an `err` banner is announced (`role="alert"`). True by default,
   * for a failure of the page or a region; false for inline validation that
   * changes as the person types. Other tones are never announced.
   */
  announce?: boolean;
  /** `sm` for a banner inside a card or a dialog; `md` by default. */
  size?: AlertRootProps["size"];
  /**
   * Title on its own line and the text under it, at every width. For a body
   * of more than one sentence (a description, tips, links). Off by default:
   * a one-line banner reads title and text as one run.
   */
  stacked?: boolean;
}

export const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  function Banner(
    {
      tone = "info",
      title,
      children,
      action,
      icon,
      size = "md",
      announce = true,
      stacked = false,
      role,
      ...rest
    },
    ref,
  ) {
    const Glyph = ICON[tone];
    return (
      <Alert.Root
        ref={ref}
        status={STATUS[tone]}
        variant="subtle"
        size={size}
        role={role ?? (tone === "err" && announce ? "alert" : "note")}
        data-tone={tone}
        flexWrap="wrap"
        {...rest}
      >
        {icon === null ? null : (
          <Alert.Indicator aria-hidden="true">
            {icon ?? <Glyph strokeWidth={1.75} />}
          </Alert.Indicator>
        )}
        <Alert.Content
          flex="1 1 calc(var(--space-8) * 6)"
          flexDirection={stacked ? "column" : "row"}
          flexWrap={stacked ? "nowrap" : "wrap"}
          alignItems={stacked ? "stretch" : "baseline"}
          columnGap="var(--space-1)"
          rowGap={stacked ? "var(--space-1)" : undefined}
          minWidth={0}
        >
          {title ? <Alert.Title>{title}</Alert.Title> : null}
          {children ? <Alert.Description>{children}</Alert.Description> : null}
        </Alert.Content>
        {action ? (
          <Box
            flexShrink={0}
            marginInlineStart="auto"
            alignSelf="center"
            display="flex"
            gap="var(--space-2)"
          >
            {action}
          </Box>
        ) : null}
      </Alert.Root>
    );
  },
);
